import type { useVersionPreviewStore, useVersionWorkspaceStore } from '@/stores';
import type {
  VersionPreviewRecord,
  VersionPreviewService,
  VersionPreviewSnapshot,
} from '@/services/version-preview';
import { logger } from '@/utils';

/**
 * 版本 Preview 重载请求上下文。
 */
export interface VersionPreviewReloadContext {
  /** 当前异步重载是否仍属于最新一次版本切换请求。 */
  isCurrent: () => boolean;
}

/**
 * 版本 Preview controller 依赖。
 */
export interface UseVersionPreviewControllerOptions {
  /** 版本 Preview 页面状态。 */
  store: ReturnType<typeof useVersionPreviewStore>;
  /** 历史预览只读上下文状态。 */
  workspaceStore: ReturnType<typeof useVersionWorkspaceStore>;
  /** 版本 Preview 服务。 */
  service: VersionPreviewService;
  /** 读取当前项目 ID，避免 controller 绑定路由实现。 */
  getProjectId: () => string;
  /** 读取路由中的版本 ID。 */
  getRouteVersionId: () => string | null;
  /** 读取当前 Viewer 已成功显示的提交 ID，用于把高亮绑定到真实模型状态。 */
  getDisplayedVersionId?: () => string | null;
  /** 成功切换后回写路由 query。 */
  replaceVersionQuery: (versionId: string) => void;
  /** 取消当前页面正在展示的历史版本重载 loading。 */
  cancelActiveReload?: () => void;
  /** 重载版本快照对应的模型与设计树。 */
  reloadSnapshot: (
    snapshot: VersionPreviewSnapshot,
    context: VersionPreviewReloadContext,
  ) => Promise<void>;
  /** 失败提示出口。 */
  notifyError: (message: string) => void;
  /** 版本选择防抖时间，测试可传 0。 */
  switchDebounceMs?: number;
}

interface ScheduledSwitchVersion {
  /** 本次调度的版本 ID。 */
  versionId: string;
  /** 本次调度成为最新 owner 时分配的请求编号。 */
  requestId: number;
  /** 结束该次 selectVersion 调用等待的 resolver。 */
  resolve: () => void;
  /** 当前 selectVersion 调用向外返回的 Promise。 */
  promise: Promise<void>;
}

/**
 * 从版本列表中解析初始版本。
 *
 * @param records 当前版本列表。
 * @param routeVersionId 路由声明的版本 ID。
 * @param displayedVersionId 当前 Viewer 已显示的版本 ID。
 * @returns 初始版本记录；没有明确目标时返回 null。
 */
function resolveInitialVersionRecord(
  records: readonly VersionPreviewRecord[],
  routeVersionId: string | null,
  displayedVersionId: string | null,
): VersionPreviewRecord | null {
  if (routeVersionId) {
    const routeRecord = records.find((record) => record.id === routeVersionId);
    if (routeRecord) {
      return routeRecord;
    }
  }

  const adoptedRecord = records.find((record) => record.isAdoptedVersion);
  if (adoptedRecord) {
    return adoptedRecord;
  }

  if (displayedVersionId) {
    return records.find((record) => record.id === displayedVersionId) ?? null;
  }

  return null;
}

/**
 * 版本 Preview 控制器。
 *
 * @param options 控制器依赖。
 * @returns 版本 Preview 初始化、选择和生命周期清理方法。
 */
export function useVersionPreviewController(options: UseVersionPreviewControllerOptions) {
  const {
    store,
    workspaceStore,
    service,
    getProjectId,
    getRouteVersionId,
    getDisplayedVersionId,
    replaceVersionQuery,
    cancelActiveReload,
    reloadSnapshot,
    notifyError,
    switchDebounceMs = 160,
  } = options;

  let isDisposed = false;
  let recordsRequestId = 0;
  let recordsAbortController: AbortController | null = null;
  let switchVersionRequestId = 0;
  let switchVersionAbortController: AbortController | null = null;
  let pendingSwitchVersionId: string | null = null;
  let switchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let scheduledSwitchVersion: ScheduledSwitchVersion | null = null;

  /**
   * 归一化版本 ID，避免空白字符串误参与高亮和跳过判断。
   *
   * @param versionId 外部状态中的版本 ID。
   * @returns 非空版本 ID；缺失或空白时返回 null。
   */
  function normalizeVersionId(versionId: string | null | undefined): string | null {
    const normalizedVersionId = versionId?.trim() ?? '';
    return normalizedVersionId || null;
  }

  /**
   * 解析当前已经落地到 Viewer 的版本 ID。
   *
   * @param fallbackVersionId loader 未暴露已显示提交时的兜底版本 ID。
   * @returns 应用于列表高亮、历史工作区状态和路由 query 的版本 ID。
   */
  function resolveDisplayedVersionId(fallbackVersionId: string): string {
    return normalizeVersionId(getDisplayedVersionId?.()) ?? fallbackVersionId;
  }

  /**
   * 判断目标版本是否已经与当前可见模型一致。
   *
   * @param versionId 用户请求切换的版本 ID。
   * @returns UI 高亮与 Viewer 实际提交都一致时返回 true。
   */
  function isVersionAlreadyDisplayed(versionId: string): boolean {
    const displayedVersionId = normalizeVersionId(getDisplayedVersionId?.());
    return store.selectedVersionId === versionId
      && (!displayedVersionId || displayedVersionId === versionId);
  }

  /**
   * 判断目标版本是否已经落地到当前 Viewer。
   *
   * @param versionId 需要确认的版本 ID。
   * @returns Viewer 已经显示该版本时返回 true。
   */
  function isVersionDisplayedInViewer(versionId: string): boolean {
    return normalizeVersionId(getDisplayedVersionId?.()) === versionId;
  }

  /**
   * 同步已经加载完成的版本预览状态。
   *
   * 设计说明：版本预览页初始化时会先通过 OpenProject + 默认标签加载根模型；当该模型
   * 已经等于初始版本时，不应再发起一次快照重载，但列表高亮、只读工作区和路由仍需补齐。
   *
   * @param versionId 已经显示在 Viewer 中的版本 ID。
   */
  function activateDisplayedVersion(versionId: string): void {
    const displayedVersionId = resolveDisplayedVersionId(versionId);
    store.switchError = null;
    store.selectVersion(displayedVersionId);
    workspaceStore.enterHistoryPreview(displayedVersionId);
    replaceVersionQuery(displayedVersionId);
  }

  /**
   * 判断版本记录请求结果是否仍属于当前 controller 生命周期。
   *
   * @param requestId 本次版本记录请求编号。
   * @returns 请求仍有效时返回 true。
   */
  function isActiveRecordsRequest(requestId: number): boolean {
    return !isDisposed && recordsRequestId === requestId;
  }

  /**
   * 中止当前版本记录加载请求。
   *
   * 设计说明：版本预览页会分页读取完整普通分支提交图，切换生命周期时主动取消，
   * 可以避免旧页面继续占用网络与后端查询资源。
   */
  function abortRecordsRequest(): void {
    if (!recordsAbortController) {
      return;
    }
    recordsAbortController.abort();
    recordsAbortController = null;
  }

  /**
   * 中止当前版本快照读取请求。
   *
   * 设计说明：版本切换真正进入模型重载后由 glbLoader 的 context/AbortSignal 接管；
   * 这里负责切换前的 snapshot/records 查询阶段，避免快速扫选版本时旧 ListCommits 继续运行。
   */
  function abortSwitchVersionRequest(): void {
    if (!switchVersionAbortController) {
      return;
    }
    switchVersionAbortController.abort();
    switchVersionAbortController = null;
  }

  /**
   * 判断版本切换请求是否仍是最新请求。
   *
   * @param requestId 本次版本切换请求编号。
   * @returns 请求仍有效时返回 true。
   */
  function isActiveSwitchVersionRequest(requestId: number): boolean {
    return !isDisposed && switchVersionRequestId === requestId;
  }

  /**
   * 清理版本切换防抖计时器。
   */
  function clearSwitchDebounceTimer(): void {
    if (!switchDebounceTimer) {
      return;
    }
    clearTimeout(switchDebounceTimer);
    switchDebounceTimer = null;
  }

  /**
   * 结束当前被防抖合并的选择 Promise。
   *
   * @param schedule 调度对象。
   */
  function resolveScheduledSwitchPromise(schedule: ScheduledSwitchVersion | null): void {
    schedule?.resolve();
    if (scheduledSwitchVersion === schedule) {
      scheduledSwitchVersion = null;
    }
  }

  /**
   * 读取当前正在尝试切换到的版本 ID。
   *
   * @returns 正在防抖或加载中的版本 ID；没有切换任务时返回 null。
   */
  function resolveActiveSwitchVersionId(): string | null {
    return pendingSwitchVersionId ?? store.switchingVersionId;
  }

  /**
   * 判断当前是否正在切换到另一个版本。
   *
   * @param versionId 用户刚刚重新选择的版本 ID。
   * @returns 存在未完成切换且目标不同于当前选择时返回 true。
   */
  function isSwitchingToDifferentVersion(versionId: string): boolean {
    const activeSwitchVersionId = resolveActiveSwitchVersionId();
    return Boolean(activeSwitchVersionId && activeSwitchVersionId !== versionId);
  }

  /**
   * 取消当前未完成的版本切换，并恢复到已经显示在 Viewer 中的版本状态。
   *
   * 设计说明：当用户在 A -> B 的加载过程中点回 A 时，A 已经是当前 Viewer 可见版本，
   * 不能被“重复点击当前版本不重载”的优化直接吞掉；这里通过推进请求序号让 B 的异步结果
   * 在返回后自然失效，并同步清理列表 loading 与只读预览状态。
   *
   * @param versionId 当前 Viewer 已显示的版本 ID。
   */
  function cancelActiveSwitchAndActivateDisplayedVersion(versionId: string): void {
    pendingSwitchVersionId = null;
    switchVersionRequestId += 1;
    abortSwitchVersionRequest();
    clearSwitchDebounceTimer();
    store.finishSwitchingVersion();
    resolveScheduledSwitchPromise(scheduledSwitchVersion);
    cancelActiveReload?.();
    activateDisplayedVersion(versionId);
  }

  /**
   * 加载版本记录并写入 Store。
   *
   * @returns 版本记录。
   */
  async function loadRecordsOrThrow(): Promise<{
    records: readonly VersionPreviewRecord[];
  } | null> {
    if (isDisposed) {
      return null;
    }

    const requestId = recordsRequestId + 1;
    recordsRequestId = requestId;
    abortRecordsRequest();
    const abortController = new AbortController();
    recordsAbortController = abortController;
    store.isRecordsLoading = true;
    store.recordsError = null;

    try {
      const records = await service.fetchVersionRecords(getProjectId(), {
        signal: abortController.signal,
      });
      if (!isActiveRecordsRequest(requestId)) {
        return null;
      }
      store.setRecords(records);
      return {
        records,
      };
    } catch (error) {
      if (!isActiveRecordsRequest(requestId)) {
        return null;
      }
      logger.error('[VersionPreview] load version records failed', {
        projectId: getProjectId(),
        error,
      });
      store.recordsError = '加载版本记录失败';
      throw error;
    } finally {
      if (recordsAbortController === abortController) {
        recordsAbortController = null;
      }
      if (isActiveRecordsRequest(requestId)) {
        store.isRecordsLoading = false;
      }
    }
  }

  /**
   * 执行版本切换。
   *
   * @param versionId 目标版本 ID。
   * @param requestId 本次请求编号。
   * @returns 是否成功切换。
   */
  async function switchVersionNow(versionId: string, requestId: number): Promise<boolean> {
    const previousVersionId = store.selectedVersionId;
    store.beginSwitchingVersion(versionId);
    store.switchError = null;

    abortSwitchVersionRequest();
    const abortController = new AbortController();
    switchVersionAbortController = abortController;

    try {
      const snapshot = await service.fetchVersionSnapshot(getProjectId(), versionId, {
        signal: abortController.signal,
      });
      if (!isActiveSwitchVersionRequest(requestId)) {
        return false;
      }

      await reloadSnapshot(snapshot, {
        isCurrent: () => isActiveSwitchVersionRequest(requestId),
      });
      if (!isActiveSwitchVersionRequest(requestId)) {
        return false;
      }

      const displayedVersionId = resolveDisplayedVersionId(versionId);
      store.selectVersion(displayedVersionId);
      workspaceStore.enterHistoryPreview(displayedVersionId);
      replaceVersionQuery(displayedVersionId);
      return true;
    } catch (error) {
      if (!isActiveSwitchVersionRequest(requestId)) {
        return false;
      }
      logger.error('[VersionPreview] switch version failed', {
        projectId: getProjectId(),
        versionId,
        error,
      });
      if (previousVersionId) {
        store.selectVersion(previousVersionId);
        workspaceStore.enterHistoryPreview(previousVersionId);
      }
      store.switchError = '切换版本失败';
      notifyError('切换版本失败');
      return false;
    } finally {
      if (switchVersionAbortController === abortController) {
        switchVersionAbortController = null;
      }
      if (isActiveSwitchVersionRequest(requestId)) {
        store.finishSwitchingVersion(versionId);
      }
    }
  }

  /**
   * 执行防抖后的最后一次版本切换。
   *
   * @param schedule 本次选择调度对象。
   */
  async function flushPendingSwitchVersion(schedule: ScheduledSwitchVersion): Promise<void> {
    const { versionId, requestId } = schedule;
    if (pendingSwitchVersionId === versionId) {
      pendingSwitchVersionId = null;
    }
    clearSwitchDebounceTimer();

    if (!versionId || !isActiveSwitchVersionRequest(requestId)) {
      resolveScheduledSwitchPromise(schedule);
      return;
    }

    try {
      await switchVersionNow(versionId, requestId);
    } finally {
      resolveScheduledSwitchPromise(schedule);
    }
  }

  /**
   * 调度版本切换。
   *
   * @param versionId 用户最后选择的版本 ID。
   * @returns 当前调度批次完成后的 Promise。
   */
  function scheduleSwitchVersion(versionId: string): Promise<void> {
    pendingSwitchVersionId = versionId;
    store.beginSwitchingVersion(versionId);
    clearSwitchDebounceTimer();

    if (scheduledSwitchVersion) {
      resolveScheduledSwitchPromise(scheduledSwitchVersion);
    }

    const requestId = switchVersionRequestId + 1;
    switchVersionRequestId = requestId;
    abortSwitchVersionRequest();
    let resolveSchedule!: () => void;
    const promise = new Promise<void>((resolve) => {
      resolveSchedule = resolve;
    });
    const schedule: ScheduledSwitchVersion = {
      versionId,
      requestId,
      resolve: resolveSchedule,
      promise,
    };
    scheduledSwitchVersion = schedule;

    if (switchDebounceMs <= 0) {
      void flushPendingSwitchVersion(schedule);
    } else {
      switchDebounceTimer = setTimeout(() => {
        void flushPendingSwitchVersion(schedule);
      }, switchDebounceMs);
    }

    return promise;
  }

  /**
   * 初始化版本 Preview 页面。
   */
  async function initialize(): Promise<void> {
    try {
      const loaded = await loadRecordsOrThrow();
      if (!loaded || isDisposed) {
        return;
      }

      const initialRecord = resolveInitialVersionRecord(
        loaded.records,
        getRouteVersionId(),
        normalizeVersionId(getDisplayedVersionId?.()),
      );
      if (!initialRecord) {
        return;
      }
      if (isVersionDisplayedInViewer(initialRecord.id)) {
        activateDisplayedVersion(initialRecord.id);
        return;
      }
      await scheduleSwitchVersion(initialRecord.id);
    } catch {
      if (!isDisposed) {
        notifyError('加载版本记录失败');
      }
    }
  }

  /**
   * 选择版本并进入只读预览。
   *
   * @param versionId 目标版本 ID。
   */
  async function selectVersion(versionId: string): Promise<void> {
    if (isDisposed) {
      return;
    }
    if (isVersionAlreadyDisplayed(versionId)) {
      if (isSwitchingToDifferentVersion(versionId)) {
        cancelActiveSwitchAndActivateDisplayedVersion(versionId);
      }
      return;
    }

    return scheduleSwitchVersion(versionId);
  }

  /**
   * 释放 controller 生命周期，阻止晚到异步结果继续回写 Store。
   */
  function dispose(): void {
    isDisposed = true;
    clearSwitchDebounceTimer();
    pendingSwitchVersionId = null;
    recordsRequestId += 1;
    abortRecordsRequest();
    switchVersionRequestId += 1;
    abortSwitchVersionRequest();
    store.isRecordsLoading = false;
    store.finishSwitchingVersion();
    resolveScheduledSwitchPromise(scheduledSwitchVersion);
    service.dispose?.();
  }

  return {
    initialize,
    selectVersion,
    dispose,
  };
}
